import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  Clipboard,
  Eye,
  Link2,
  Loader2,
  LockKeyhole,
  Save,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import PublicFormFieldPalette from './PublicFormFieldPalette';
import PublicFormPreview from './PublicFormPreview';
import PublicFormCaseSelect from './PublicFormCaseSelect';
import PublicFormLinks from './PublicFormLinks';
import { Checkbox, SearchSelect } from './FieldComponents';
import { isEligiblePublicFormRecipient } from '../utils/publicFormRecipientAccess';
import { publicFormsApi, publicFormFeatureError } from '../services/publicFormsApi';
import {
  buildPublicFormLink,
  buildPublicFormMutation,
  createCustomPublicFormField,
  createEmptyPublicFormDraft,
  createPublicFormContentItem,
  createSelectedPublicFormField,
  createUploadPublicFormField,
  movePublicFormField,
  normalizePublicFormPageUrl,
  normalizePublicFormDraft,
  parsePublicFormOptionLines,
  publicFormFieldTypeTitle,
  publicFormTargetLabel,
  publicFormTargetSupportsUploads,
  CUSTOM_QUESTION_TYPE_OPTIONS,
  isStandalonePublicFormField,
  isPublicFormContentItem,
  isPublicFormStepContentItem,
  sanitizePublicFormCreationOptions,
  validatePublicFormCreationSettings,
  validatePublicFormDraft,
} from '../utils/publicFormBuilder';

const BUILDER_STEPS = [
  { id: 'setup', label: 'Setup' },
  { id: 'fields', label: 'Fields' },
  { id: 'delivery', label: 'Link & alerts' },
  { id: 'review', label: 'Review' },
];

const CREATION_SETTING_FIELDS = [
  { key: 'assigned_user_id', label: 'Assigned user', options: 'users', placeholder: 'Choose an assigned user', required: true },
  { key: 'assigned_attorney_id', label: 'Assigned attorney', options: 'attorneys', placeholder: 'No assigned attorney', required: false },
  { key: 'location_id', label: 'Location', options: 'locations', placeholder: 'Choose a location', required: true },
];

const DESTINATION_OPTIONS = Object.freeze([
  {
    id: 'existing_case',
    label: 'Existing case',
    description: 'Publish once, then assign each new link to the case it is sent from.',
  },
  {
    id: 'existing_lead',
    label: 'Existing lead',
    description: 'Publish once, then assign each new link to the lead it is sent from.',
  },
  {
    id: 'create_case',
    label: 'Create a new case',
    description: 'Use an approved submission to start a new case record.',
  },
  {
    id: 'create_lead',
    label: 'Create a new lead',
    description: 'Use an approved submission to start a new lead record.',
  },
]);

const normalizeFormStep = (value) => Math.max(1, Math.min(20, Number.parseInt(value, 10) || 1));

const normalizeRecipient = (recipient) => ({
  id: Number(recipient?.id ?? recipient?.user_id),
  name: String(recipient?.name ?? recipient?.full_name ?? recipient?.email ?? 'CaseDB user'),
  email: String(recipient?.user_email ?? recipient?.email ?? ''),
  access_level: String(recipient?.access_level ?? '').trim().toLowerCase(),
  eligible: recipient?.eligible !== false,
});

const renderRecipientOption = (recipient) => <span className="public-form-recipient-option">
  <strong className="small-text">{recipient.name}</strong>
  {recipient.email && <small>{recipient.email}</small>}
</span>;

const utf8ByteLength = (value) => {
  let length = 0;
  for (const character of String(value ?? '')) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x7f) length += 1;
    else if (codePoint <= 0x7ff) length += 2;
    else if (codePoint <= 0xffff) length += 3;
    else length += 4;
  }
  return length;
};

const PublicFormSelect = ({
  id,
  label,
  className = '',
  children,
  help = null,
  ...selectProps
}) => (
  <div className={`form-group filter-group nm${className ? ` ${className}` : ''}`}>
    <label className="subtext" htmlFor={id}>{label}</label>
    <div className="select-wrapper">
      <select id={id} className="filter-select" {...selectProps}>{children}</select>
      <ChevronDown size={16} className="select-chevron" aria-hidden="true" />
    </div>
    {help}
  </div>
);

const PublicFormBuilder = ({
  initialForm = null,
  catalog = [],
  caseTypes = [],
  onCaseTypeChange,
  notificationRecipients = [],
  creationOptions = {},
  capabilities = {},
  endpoint,
  publicFormBaseUrl,
  onCancel,
  onSaved,
}) => {
  const [draft, setDraft] = useState(() => normalizePublicFormDraft(initialForm || createEmptyPublicFormDraft()));
  const [stepIndex, setStepIndex] = useState(0);
  const [activeFormStep, setActiveFormStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [requestError, setRequestError] = useState('');
  const [saving, setSaving] = useState(false);
  const [creationSettingsDirty, setCreationSettingsDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [publicationCurrent, setPublicationCurrent] = useState(
    draft.status === 'published' && draft.publication_current === true && !draft.requires_republish,
  );
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [issuedLink, setIssuedLink] = useState(null);
  const [showLinkHistory, setShowLinkHistory] = useState(false);
  const [copyState, setCopyState] = useState('');
  const [collapsedFieldIds, setCollapsedFieldIds] = useState(() => new Set());
  const [previewDraft, setPreviewDraft] = useState(null);
  const [focusTarget, setFocusTarget] = useState(false);
  const [selectedTargetCase, setSelectedTargetCase] = useState(null);
  const targetInputRef = useRef(null);
  const issuanceGenerationRef = useRef(0);
  const issuedLinkRef = useRef(null);
  const issuedLinkCopyRef = useRef(null);
  const [linkSettings, setLinkSettings] = useState(() => ({
    target_id: draft.target_id,
    expires_in_days: 7,
    max_uses: draft.max_submissions || 1,
    unlimited_submissions: false,
    password_protected: false,
    password: '',
  }));

  const recipients = useMemo(
    () => {
      // The picker catalog excludes the current editor and is capped. Saved
      // recipients also carry current user roles from the authorized form GET;
      // retain eligible saved recipients even when absent from that catalog.
      const configured = Array.isArray(initialForm?.notification_recipients) ? initialForm.notification_recipients : [];
      const byId = new Map([...configured, ...notificationRecipients].map((recipient) => [
        Number(recipient?.id ?? recipient?.user_id), recipient,
      ]));
      return [...byId.values()].filter(isEligiblePublicFormRecipient).map(normalizeRecipient);
    },
    [initialForm?.notification_recipients, notificationRecipients],
  );
  const availableCreationOptions = useMemo(
    () => sanitizePublicFormCreationOptions(creationOptions),
    [creationOptions],
  );
  const isCreationMode = ['create_case', 'create_lead'].includes(draft.target_mode);
  const selectedRecipientIds = useMemo(() => new Set([
    ...draft.notification_in_app_user_ids, ...draft.notification_email_user_ids,
  ]), [draft.notification_in_app_user_ids, draft.notification_email_user_ids]);
  const selectedRecipients = recipients.filter((recipient) => selectedRecipientIds.has(recipient.id));
  const unavailableRecipientIds = [...selectedRecipientIds].filter((id) => !recipients.some((recipient) => recipient.id === id));
  const recipientOptions = recipients.filter((recipient) => !selectedRecipientIds.has(recipient.id)).map((recipient) => ({
    ...recipient, value: String(recipient.id), label: `${recipient.name} ${recipient.email}`.trim(),
  }));
  const catalogByKey = useMemo(
    () => new Map(catalog.map((field) => [field.field_key || field.id, field])),
    [catalog],
  );
  const availableCatalog = useMemo(
    () => catalog.filter((field) => (
      !Array.isArray(field.applicable_modes)
      || field.applicable_modes.length === 0
      || field.applicable_modes.includes(draft.target_mode)
    )),
    [catalog, draft.target_mode],
  );
  const incompatibleFields = useMemo(
    () => draft.fields.filter((field) => {
      if (!draft.case_type_id || catalogLoading) return false;
      if (isStandalonePublicFormField(field)) return false;
      const catalogField = catalogByKey.get(field.field_key);
      return !catalogField || (
        Array.isArray(catalogField.applicable_modes)
        && catalogField.applicable_modes.length > 0
        && !catalogField.applicable_modes.includes(draft.target_mode)
      );
    }),
    [catalogByKey, catalogLoading, draft.case_type_id, draft.fields, draft.target_mode],
  );
  const highestFormStep = useMemo(
    () => Math.max(activeFormStep, 1, ...draft.fields.map((field) => normalizeFormStep(field.step))),
    [activeFormStep, draft.fields],
  );
  const formSteps = useMemo(
    () => Array.from({ length: highestFormStep }, (_, index) => index + 1),
    [highestFormStep],
  );
  const visibleFieldEntries = useMemo(
    () => draft.fields
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => (
        normalizeFormStep(field.step) === activeFormStep
        && !isPublicFormStepContentItem(field)
      )),
    [activeFormStep, draft.fields],
  );
  const activeStepTitle = useMemo(
    () => draft.fields.find((item) => (
      isPublicFormStepContentItem(item)
      && item.content_type === 'step_title'
      && normalizeFormStep(item.step) === activeFormStep
    ))?.text || '',
    [activeFormStep, draft.fields],
  );
  const activeStepInstructions = useMemo(
    () => draft.fields.find((item) => (
      isPublicFormStepContentItem(item)
      && item.content_type === 'step_instruction'
      && normalizeFormStep(item.step) === activeFormStep
    ))?.text || '',
    [activeFormStep, draft.fields],
  );
  const activeStep = BUILDER_STEPS[stepIndex];
  useEffect(() => {
    if (!focusTarget || activeStep.id !== 'delivery') return;
    targetInputRef.current?.focus();
    targetInputRef.current?.scrollIntoView?.({ block: 'nearest' });
    setFocusTarget(false);
  }, [activeStep.id, focusTarget]);
  const questionFields = draft.fields.filter((field) => !isPublicFormContentItem(field));
  const standaloneQuestions = questionFields.filter(isStandalonePublicFormField).length;
  const placeholderLabels = questionFields.filter((field) => /^(custom question|untitled field)$/i.test(field.label.trim())).length;
  const creationEnabled = capabilities.creation_enabled === true;
  const creationSettingsEnabled = capabilities.creation_settings_enabled === true;
  const hasCreationSettings = isCreationMode && creationSettingsEnabled;
  const canSelectCreationMode = capabilities.can_select_creation_mode === true;
  const publicRuntimeEnabled = capabilities.public_enabled === true;
  const passwordLinksEnabled = capabilities.password_links_enabled === true;
  const creationLinkEnabled = !draft.target_mode.startsWith('create_')
    || capabilities.creation_links_enabled === true;
  const uploadsEnabled = capabilities.uploads?.enabled === true && capabilities.uploads_enabled === true;
  const customQuestionsEnabled = capabilities.custom_questions?.enabled === true
    && capabilities.custom_questions?.namespace === 'question.';
  const inAppNotificationsEnabled = capabilities.notifications?.in_app === true;
  const emailEnabled = capabilities.notifications?.email === true || capabilities.email_notifications_enabled === true;

  const clearIssuedLink = () => {
    setIssuedLink(null);
    setCopyState('');
  };

  useEffect(() => {
    if (!issuedLink) return;
    const issuedLinkElement = issuedLinkRef.current;
    const copyButton = issuedLinkCopyRef.current;
    if (issuedLinkElement && typeof issuedLinkElement.scrollIntoView === 'function') {
      issuedLinkElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    if (copyButton && typeof copyButton.focus === 'function') {
      copyButton.focus({ preventScroll: true });
    }
  }, [issuedLink]);

  const invalidateIssuance = () => {
    issuanceGenerationRef.current += 1;
    clearIssuedLink();
  };

  const updateLinkSettings = (updater) => {
    setLinkSettings((current) => (typeof updater === 'function' ? updater(current) : { ...current, ...updater }));
    invalidateIssuance();
  };

  const setDraftValue = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({
      ...current,
      [key]: undefined,
      ...(key === 'target_mode' ? { target_id: undefined } : {}),
    }));
    setPublicationCurrent(false);
    if (key === 'target_mode') {
      setSelectedTargetCase(null);
      setLinkSettings((current) => ({ ...current, target_id: '' }));
      invalidateIssuance();
    }
  };

  const setCreationSetting = (key, value) => {
    setDraft((current) => ({
      ...current,
      creation_settings: { ...current.creation_settings, [key]: value },
    }));
    setErrors((current) => ({
      ...current,
      creation_settings: { ...current.creation_settings, [key]: undefined },
    }));
    setCreationSettingsDirty(true);
    invalidateIssuance();
  };

  const checkCreationSettings = () => {
    // Old APIs cannot persist private defaults or provide their option lists.
    if (!hasCreationSettings) return true;
    const validation = validatePublicFormCreationSettings(draft, availableCreationOptions);
    if (!validation.valid) {
      setErrors((current) => ({ ...current, creation_settings: validation.errors }));
      setRequestError('Choose the new-record defaults in Setup before publishing or creating a link.');
      setStepIndex(0);
    }
    return validation.valid;
  };

  const changeCaseType = async (value) => {
    const caseTypeId = String(value || '');
    if (caseTypeId === draft.case_type_id) return;
    setDraft((current) => ({ ...current, case_type_id: caseTypeId }));
    setSelectedTargetCase(null);
    setLinkSettings((current) => ({ ...current, target_id: '' }));
    setErrors((current) => ({ ...current, case_type_id: undefined, target_id: undefined, fields: undefined }));
    invalidateIssuance();
    setPublicationCurrent(false);
    setCatalogLoading(true);
    setRequestError('');
    try {
      await onCaseTypeChange?.(caseTypeId);
    } catch (error) {
      setRequestError(publicFormFeatureError(error));
    } finally {
      setCatalogLoading(false);
    }
  };

  const addField = (catalogField) => {
    setDraft((current) => {
      const nextField = createSelectedPublicFormField(catalogField, current.fields);
      return nextField ? {
        ...current,
        fields: [...current.fields, { ...nextField, step: activeFormStep }],
      } : current;
    });
    setErrors((current) => ({ ...current, fields: undefined }));
    setPublicationCurrent(false);
  };

  const addUploadField = () => {
    if (!uploadsEnabled || !publicFormTargetSupportsUploads(draft.target_mode)) return;
    setDraft((current) => ({
      ...current,
      fields: [...current.fields, { ...createUploadPublicFormField(current.fields), step: activeFormStep }],
    }));
    setPublicationCurrent(false);
  };

  const addCustomField = (type) => {
    if (!customQuestionsEnabled) return;
    setDraft((current) => ({
      ...current,
      fields: [...current.fields, { ...createCustomPublicFormField(type, current.fields), step: activeFormStep }],
    }));
    setErrors((current) => ({ ...current, fields: undefined }));
    setPublicationCurrent(false);
  };

  const addContentItem = (type) => {
    setDraft((current) => ({
      ...current,
      fields: [...current.fields, { ...createPublicFormContentItem(type, current.fields), step: activeFormStep }],
    }));
    setPublicationCurrent(false);
  };

  const updateStepContent = (type, value) => {
    const text = String(value ?? '');
    setDraft((current) => {
      const existingIndex = current.fields.findIndex((item) => (
        isPublicFormStepContentItem(item)
        && item.content_type === type
        && normalizeFormStep(item.step) === activeFormStep
      ));
      if (!text.trim()) {
        return existingIndex < 0
          ? current
          : { ...current, fields: current.fields.filter((_, index) => index !== existingIndex) };
      }
      if (existingIndex >= 0) {
        return {
          ...current,
          fields: current.fields.map((item, index) => index === existingIndex ? { ...item, text } : item),
        };
      }
      const item = {
        ...createPublicFormContentItem(type, current.fields),
        text,
        step: activeFormStep,
      };
      const firstStepItem = current.fields.findIndex((candidate) => normalizeFormStep(candidate.step) === activeFormStep);
      const insertAt = firstStepItem < 0 ? current.fields.length : firstStepItem;
      const fields = [...current.fields];
      fields.splice(insertAt, 0, item);
      return { ...current, fields };
    });
    setErrors((current) => ({ ...current, fields: undefined }));
    setPublicationCurrent(false);
  };

  const updateField = (index, patch) => {
    setDraft((current) => ({
      ...current,
      fields: current.fields.map((field, fieldIndex) => fieldIndex === index ? { ...field, ...patch } : field),
    }));
    setErrors((current) => ({ ...current, fields: undefined }));
    setPublicationCurrent(false);
  };

  const updateFieldStep = (index, value) => {
    const nextStep = normalizeFormStep(value);
    setDraft((current) => {
      const priorStep = normalizeFormStep(current.fields[index]?.step);
      let continueCascade = true;
      return {
        ...current,
        fields: current.fields.map((field, fieldIndex) => {
          if (fieldIndex < index || !continueCascade) return field;
          if (fieldIndex === index || normalizeFormStep(field.step) === priorStep) {
            return { ...field, step: nextStep };
          }
          continueCascade = false;
          return field;
        }),
      };
    });
    setActiveFormStep(nextStep);
    setErrors((current) => ({ ...current, fields: undefined }));
    setPublicationCurrent(false);
  };

  const removeField = (index) => {
    const removedInstanceId = draft.fields[index]?.instance_id;
    setDraft((current) => ({
      ...current,
      fields: current.fields.filter((field, fieldIndex) => fieldIndex !== index),
    }));
    if (removedInstanceId) {
      setCollapsedFieldIds((current) => {
        const next = new Set(current);
        next.delete(removedInstanceId);
        return next;
      });
    }
    setPublicationCurrent(false);
  };

  const toggleFieldCollapsed = (instanceId) => {
    setCollapsedFieldIds((current) => {
      const next = new Set(current);
      if (next.has(instanceId)) next.delete(instanceId); else next.add(instanceId);
      return next;
    });
  };

  const moveField = (fromIndex, toIndex) => {
    setDraft((current) => ({ ...current, fields: movePublicFormField(current.fields, fromIndex, toIndex) }));
    setPublicationCurrent(false);
  };

  const moveFieldWithinStep = (fieldIndex, direction) => {
    const visibleIndex = visibleFieldEntries.findIndex((entry) => entry.index === fieldIndex);
    const target = visibleFieldEntries[visibleIndex + direction];
    if (!target) return;
    moveField(fieldIndex, target.index);
  };

  const stepCanAdvance = () => {
    const nextErrors = {};
    if (activeStep.id === 'setup') {
      if (!draft.name.trim()) nextErrors.name = 'Give this form an internal name.';
      if (!draft.title.trim()) nextErrors.title = 'Add a public title.';
      if (!draft.case_type_id) nextErrors.case_type_id = 'Choose the case type this form belongs to.';
    }
    if (activeStep.id === 'fields' && !draft.fields.some((field) => !isPublicFormContentItem(field))) {
      nextErrors.fields = 'Add at least one question.';
    }
    if (activeStep.id === 'fields' && incompatibleFields.length > 0) {
      nextErrors.fields = `Remove fields that are unavailable for ${publicFormTargetLabel(draft.target_mode).toLowerCase()}: ${incompatibleFields.map((field) => field.label).join(', ')}.`;
    }
    setErrors((current) => ({ ...current, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const saveDraft = async () => {
    if (unavailableRecipientIds.length > 0) {
      setRequestError('Remove unavailable notification recipients in Link & alerts before saving.');
      setStepIndex(BUILDER_STEPS.findIndex((step) => step.id === 'delivery'));
      return null;
    }
    if (incompatibleFields.length > 0) {
      setErrors((current) => ({
        ...current,
        fields: `Remove fields that are unavailable for ${publicFormTargetLabel(draft.target_mode).toLowerCase()}: ${incompatibleFields.map((field) => field.label).join(', ')}.`,
      }));
      setRequestError('This destination cannot accept every selected field.');
      return null;
    }
    const validation = validatePublicFormDraft({
      ...draft,
      target_id: linkSettings.target_id,
      max_submissions: linkSettings.max_uses,
    });
    if (!validation.valid) {
      setErrors(validation.errors);
      setRequestError('Review the highlighted settings before saving.');
      return null;
    }
    setSaving(true);
    setRequestError('');
    try {
      const mutation = buildPublicFormMutation(validation.draft);
      if (!creationSettingsEnabled) delete mutation.creation_settings;
      const payload = validation.draft.id
        ? await publicFormsApi.update(validation.draft.id, mutation, { endpoint })
        : await publicFormsApi.create(mutation, { endpoint });
      const savedForm = payload.form || payload.public_form || { ...validation.draft, id: payload.form_id || payload.id };
      const nextDraft = normalizePublicFormDraft({ ...validation.draft, ...savedForm });
      setDraft(nextDraft);
      setCreationSettingsDirty(false);
      setPublicationCurrent(nextDraft.publication_current === true && !nextDraft.requires_republish);
      onSaved?.(nextDraft);
      return nextDraft;
    } catch (error) {
      setRequestError(publicFormFeatureError(error));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!checkCreationSettings()) return;
    setPublishing(true);
    const saved = await saveDraft();
    if (!saved?.id) {
      setPublishing(false);
      return;
    }
    try {
      const payload = await publicFormsApi.publish(saved.id, { endpoint });
      const publishedForm = normalizePublicFormDraft({ ...saved, ...(payload.form || {}) });
      setDraft(publishedForm);
      setPublicationCurrent(publishedForm.publication_current === true && !publishedForm.requires_republish);
      onSaved?.(publishedForm);
    } catch (error) {
      setRequestError(publicFormFeatureError(error));
    } finally {
      setPublishing(false);
    }
  };

  const issueLink = async () => {
    if (!draft.id || !publicationCurrent || !publicRuntimeEnabled || !creationLinkEnabled || (hasCreationSettings && creationSettingsDirty)) {
      setLinkSettings((current) => ({ ...current, password: '' }));
      return;
    }
    if (!checkCreationSettings()) return;
    if (['existing_case', 'existing_lead'].includes(draft.target_mode) && !Number(linkSettings.target_id)) {
      setErrors((current) => ({ ...current, target_id: draft.target_mode === 'existing_case' ? 'Choose a case for this link.' : 'This form mode requires a target ID for each link.' }));
      setLinkSettings((current) => ({ ...current, password: '' }));
      return;
    }
    const expiresInDays = Number(linkSettings.expires_in_days);
    const maxUses = Number(linkSettings.max_uses);
    if (!Number.isInteger(expiresInDays) || expiresInDays < 1 || expiresInDays > 3650) {
      setErrors((current) => ({ ...current, expires_in_days: 'Choose a whole number from 1 to 3,650 days.' }));
      return;
    }
    if (!linkSettings.unlimited_submissions && (!Number.isInteger(maxUses) || maxUses < 1 || maxUses > 1000)) {
      setErrors((current) => ({ ...current, max_uses: 'Choose a whole number from 1 to 1,000 submissions.' }));
      return;
    }
    const passwordBytes = utf8ByteLength(linkSettings.password);
    if (linkSettings.password_protected && (
      !passwordLinksEnabled
      || passwordBytes < 12
      || passwordBytes > 256
      || !/\S/u.test(linkSettings.password)
    )) {
      setErrors((current) => ({
        ...current,
        password: passwordLinksEnabled
          ? 'Use a password between 12 and 256 bytes with at least one non-space character.'
          : 'Password-protected links are disabled in this environment.',
      }));
      setLinkSettings((current) => ({ ...current, password: '' }));
      return;
    }
    const publicPageUrl = normalizePublicFormPageUrl(publicFormBaseUrl);
    if (!publicPageUrl) {
      setRequestError('CaseDB cannot create a recipient link until the public form URL is configured correctly.');
      setLinkSettings((current) => ({ ...current, password: '' }));
      return;
    }
    issuanceGenerationRef.current += 1;
    const requestGeneration = issuanceGenerationRef.current;
    const issuanceSnapshot = Object.freeze({
      targetMode: draft.target_mode,
      targetId: linkSettings.target_id,
      expiresInDays,
      maxUses,
      unlimitedSubmissions: Boolean(linkSettings.unlimited_submissions),
      passwordProtected: Boolean(linkSettings.password_protected),
      password: linkSettings.password,
    });
    clearIssuedLink();
    setIssuing(true);
    setRequestError('');
    try {
      const payload = await publicFormsApi.issueLink(draft.id, {
        target_id: ['existing_case', 'existing_lead'].includes(issuanceSnapshot.targetMode)
          ? Number(issuanceSnapshot.targetId)
          : null,
        expires_in_days: issuanceSnapshot.expiresInDays,
        unlimited_submissions: issuanceSnapshot.unlimitedSubmissions,
        ...(issuanceSnapshot.unlimitedSubmissions ? {} : { max_uses: issuanceSnapshot.maxUses }),
        password_protected: issuanceSnapshot.passwordProtected,
        ...(issuanceSnapshot.passwordProtected ? { password: issuanceSnapshot.password } : {}),
      }, { endpoint });
      if (issuanceGenerationRef.current !== requestGeneration) return;
      const fragment = payload.fragment || (payload.token ? `#${payload.token}` : '');
      const link = buildPublicFormLink(fragment, publicPageUrl);
      if (!link) throw new Error('The form was published, but CaseDB could not build a public URL in this environment.');
      setIssuedLink(Object.freeze({
        id: payload.link?.id,
        url: link,
        passwordProtected: issuanceSnapshot.passwordProtected,
      }));
      setDraft((current) => ({ ...current, link_count: current.link_count + 1 }));
      setCopyState('');
    } catch (error) {
      if (issuanceGenerationRef.current === requestGeneration) {
        setRequestError(publicFormFeatureError(error));
      }
    } finally {
      setLinkSettings((current) => (
        issuanceGenerationRef.current === requestGeneration
          && current.password === issuanceSnapshot.password
          ? { ...current, password: '' }
          : current
      ));
      setIssuing(false);
    }
  };

  const copyLink = async () => {
    if (!issuedLink) return;
    try {
      await navigator.clipboard.writeText(issuedLink.url);
      setCopyState('Copied');
    } catch (error) {
      setCopyState('Select and copy the link below');
    }
  };

  const toggleRecipient = (channel, userId) => {
    if (saving || publishing) return;
    if (!recipients.some((recipient) => recipient.id === userId)) return;
    const key = channel === 'email' ? 'notification_email_user_ids' : 'notification_in_app_user_ids';
    setDraft((current) => {
      const selected = new Set(current[key]);
      if (selected.has(userId)) selected.delete(userId); else selected.add(userId);
      return { ...current, [key]: [...selected] };
    });
    setPublicationCurrent(false);
  };

  const removeRecipients = (ids) => {
    setDraft((current) => ({
      ...current,
      notification_in_app_user_ids: current.notification_in_app_user_ids.filter((id) => !ids.includes(id)),
      notification_email_user_ids: current.notification_email_user_ids.filter((id) => !ids.includes(id)),
    }));
    setRequestError('');
    setPublicationCurrent(false);
  };

  const conditionCandidates = (index) => draft.fields
    .slice(0, index)
    .filter((field) => !isPublicFormContentItem(field));

  return (
    <div className={`public-form-builder${!showLinkHistory && activeStep.id === 'fields' ? ' public-form-builder-fields-step' : ''}`}>
      <header id="page-header" className="public-forms-header public-form-builder-header">
        <button type="button" className="action-micro quaternary" onClick={onCancel} aria-label="Back to forms">
          <ArrowLeft size={18} />
        </button>
        <div className="public-forms-header-copy">
          <h2>{draft.id ? draft.name || 'Edit form' : 'Create a public form'}</h2>
          <p className="subtext">Set up your form, choose fields, and review recipient link settings.</p>
        </div>
        <span className={`public-form-status public-form-status-${publicationCurrent ? 'published' : 'draft'}`}>
          {publicationCurrent ? 'Published' : draft.status === 'published' ? 'Changes need republishing' : 'Draft'}
        </span>
        {draft.id && <button type="button" className="action quaternary" onClick={() => setShowLinkHistory((value) => !value)} disabled={saving || publishing || issuing} aria-pressed={showLinkHistory}>
          <Link2 size={16} /> {showLinkHistory ? 'Edit form' : 'Recipient links'}
        </button>}
        <button type="button" className="action quaternary" onClick={() => setPreviewDraft(draft)} disabled={!questionFields.length}>
          <Eye size={16} /> Preview form
        </button>
      </header>

      {!showLinkHistory && <div className="public-form-builder-progress" role="navigation" aria-label="Form builder steps">
        {BUILDER_STEPS.map((step, index) => (
          <button
            key={step.id}
            type="button"
            className={`action-tiny${index === stepIndex ? ' active' : ' quaternary'}${index < stepIndex ? ' complete' : ''}`}
            onClick={() => {
              if (index <= stepIndex || stepCanAdvance()) setStepIndex(index);
            }}
            aria-current={index === stepIndex ? 'step' : undefined}
          >
            <span>{index < stepIndex ? <Check size={14} /> : index + 1}</span>
            {step.label}
          </button>
        ))}
      </div>}

      {requestError && <div className="public-form-error" role="alert">{requestError}</div>}

      <div className="public-form-builder-body">
        {showLinkHistory && <PublicFormLinks
          form={draft}
          endpoint={endpoint}
          onBack={() => setShowLinkHistory(false)}
          onCreateLink={() => {
            setShowLinkHistory(false);
            setStepIndex(BUILDER_STEPS.findIndex((step) => step.id === 'delivery'));
            setFocusTarget(true);
          }}
          onRevoked={(linkId) => {
            // A revoke can finish after returning to the builder and issuing a
            // different link. Never discard that newer link's one-time secret.
            setIssuedLink((current) => String(current?.id) === String(linkId) ? null : current);
          }}
        />}
        {!showLinkHistory && activeStep.id === 'setup' && (
          <section className="public-form-builder-panel" aria-labelledby="public-form-setup-title">
            <div className="public-form-panel-heading">
              <div className="sub-title">
                <h4 id="public-form-setup-title">Purpose and destination</h4>
              </div>
              <p className="subtext">Internal names stay in CaseDB. Public titles and descriptions appear on the recipient’s form.</p>
            </div>
            <div className="public-form-two-column">
              <div className={`form-group nm${errors.name ? ' has-error' : ''}`}>
                <label className="subtext" htmlFor="public-form-name">Internal form name</label>
                <input
                  id="public-form-name"
                  type="text"
                  value={draft.name}
                  onChange={(event) => setDraftValue('name', event.target.value)}
                  maxLength={160}
                  placeholder="e.g., New client intake — Lubbock"
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={`public-form-name-help${errors.name ? ' public-form-name-error' : ''}`}
                />
                <small id="public-form-name-help">Only CaseDB staff see this name.</small>
                {errors.name && <small id="public-form-name-error" role="alert">{errors.name}</small>}
              </div>
              <div className={`form-group nm${errors.title ? ' has-error' : ''}`}>
                <label className="subtext" htmlFor="public-form-title">Public title</label>
                <input
                  id="public-form-title"
                  type="text"
                  value={draft.title}
                  onChange={(event) => setDraftValue('title', event.target.value)}
                  maxLength={200}
                  placeholder="e.g., Tell us about your potential case"
                  aria-invalid={Boolean(errors.title)}
                  aria-describedby={`public-form-title-help${errors.title ? ' public-form-title-error' : ''}`}
                />
                <small id="public-form-title-help">Recipients see this title at the top of the form.</small>
                {errors.title && <small id="public-form-title-error" role="alert">{errors.title}</small>}
              </div>
            </div>
            <div className="form-group nm full">
              <label className="subtext" htmlFor="public-form-introduction">Public introduction</label>
              <textarea
                id="public-form-introduction"
                value={draft.description}
                onChange={(event) => setDraftValue('description', event.target.value)}
                rows={3}
                maxLength={1500}
                placeholder="Briefly explain what you need and how the recipient should complete this form."
                aria-describedby="public-form-introduction-help"
              />
              <small id="public-form-introduction-help">Keep this short and use language a client will understand.</small>
            </div>
            <PublicFormSelect
              id="public-form-case-type"
              label="Case type"
              className={`full${errors.case_type_id ? ' has-error' : ''}`}
              value={draft.case_type_id}
              onChange={(event) => changeCaseType(event.target.value)}
                disabled={catalogLoading || draft.link_count > 0}
                aria-invalid={Boolean(errors.case_type_id)}
                aria-describedby={`public-form-case-type-help${errors.case_type_id ? ' public-form-case-type-error' : ''}`}
              help={(
                <>
                  <small id="public-form-case-type-help">This controls which writable Reports fields are available next.</small>
                  {catalogLoading && <small>Loading fields for this case type…</small>}
                  {draft.link_count > 0 && <small>The case type is locked because this form already has issued links.</small>}
                  {errors.case_type_id && <small id="public-form-case-type-error" role="alert">{errors.case_type_id}</small>}
                </>
              )}
            >
                <option value="">Choose a case type</option>
                {caseTypes.map((caseType) => (
                  <option key={caseType.id} value={caseType.id}>{caseType.name}</option>
                ))}
            </PublicFormSelect>
            <fieldset className="public-form-destination-fieldset">
              <legend className="sub-title"><h4>Where should a completed form go?</h4></legend>
              <p className="public-form-fieldset-help">Choose the CaseDB record that should receive each submission.</p>
              <div className="public-form-destination-options">
                {DESTINATION_OPTIONS.map((option) => {
                  const isCreationMode = option.id.startsWith('create_');
                  const isDisabled = isCreationMode && (!creationEnabled || !canSelectCreationMode);
                  const isSelected = draft.target_mode === option.id;
                  return (
                    <div
                      key={option.id}
                      className={`form-group nm public-form-destination-option${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                      onClick={() => {
                        if (!isDisabled) setDraftValue('target_mode', option.id);
                      }}
                    >
                      <Checkbox
                        value={isSelected}
                        onChange={() => setDraftValue('target_mode', option.id)}
                        label={option.label}
                        ariaLabel={option.label}
                        ariaDescribedBy={`public-form-destination-${option.id}-description${isDisabled ? ` public-form-destination-${option.id}-availability` : ''}`}
                        disabled={isDisabled}
                      />
                      <small id={`public-form-destination-${option.id}-description`} className="public-form-destination-description">
                        {option.description}
                      </small>
                      {isCreationMode && (
                        <span id={`public-form-destination-${option.id}-availability`} className="public-form-destination-availability">
                          {!canSelectCreationMode
                            ? (isSelected && draft.id ? 'Previously configured; retained for this form' : 'Global admin only')
                            : creationEnabled ? 'Available' : 'Creation unavailable'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </fieldset>
            {hasCreationSettings && (
              <fieldset>
                <legend className="sub-title"><h4>New-record defaults</h4></legend>
                <p className="public-form-fieldset-help">
                  Choose the assignments and location for records created by this form. Assigned user and location are required before publishing or creating a link; assigned attorney is optional. Saved changes apply to new links only. Existing links keep their original defaults.
                </p>
                <div className="public-form-two-column">
                  {CREATION_SETTING_FIELDS.map((setting) => {
                    const options = availableCreationOptions[setting.options];
                    const value = draft.creation_settings[setting.key];
                    const error = errors.creation_settings?.[setting.key];
                    const unavailable = value && !options.some((option) => option.id === value);
                    const id = `public-form-creation-${setting.key}`;
                    return (
                      <PublicFormSelect
                        key={setting.key}
                        id={id}
                        label={<>{setting.label}{setting.required
                          ? <> <span className="required" aria-hidden="true">*</span></>
                          : <span aria-hidden="true"> (optional)</span>}</>}
                        className={error ? ' has-error' : ''}
                        value={value}
                        disabled={saving || publishing || catalogLoading}
                        onChange={(event) => setCreationSetting(setting.key, event.target.value)}
                        aria-required={setting.required}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error || unavailable ? `${id}-help` : undefined}
                        help={(error || unavailable) && (
                          <small id={`${id}-help`} role={error ? 'alert' : undefined}>
                            {error || 'This saved selection is unavailable. Choose an available option before creating new links.'}
                          </small>
                        )}
                      >
                        <option value="">{setting.placeholder}</option>
                        {unavailable && <option value={value} disabled>Unavailable selection (ID {value})</option>}
                        {options.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
                      </PublicFormSelect>
                    );
                  })}
                </div>
              </fieldset>
            )}
          </section>
        )}

        {!showLinkHistory && activeStep.id === 'fields' && (
          <section className="public-form-field-layout" aria-labelledby="public-form-fields-title">
            <PublicFormFieldPalette
              catalog={availableCatalog}
              selectedFields={draft.fields}
              onAdd={addField}
              onAddCustom={addCustomField}
              onAddContent={addContentItem}
              onAddUpload={addUploadField}
              customQuestionsEnabled={customQuestionsEnabled}
              uploadsEnabled={uploadsEnabled}
              targetMode={draft.target_mode}
            />
            <div className="public-form-selected-fields">
              <div className="public-form-panel-heading">
                <div className="sub-title">
                  <h4 id="public-form-fields-title">Recipient questions</h4>
                </div>
                <p className="subtext">{visibleFieldEntries.length} on form step {activeFormStep} · {draft.fields.length} total</p>
              </div>
              <div className="public-form-step-pages">
                <div className="public-form-step-tabs" role="tablist" aria-label="Form steps">
                  {formSteps.map((formStep) => {
                    const itemCount = draft.fields.filter((field) => (
                      normalizeFormStep(field.step) === formStep
                      && !isPublicFormStepContentItem(field)
                    )).length;
                    return (
                      <button
                        type="button"
                        role="tab"
                        id={`public-form-step-tab-${formStep}`}
                        aria-controls={`public-form-step-panel-${formStep}`}
                        aria-selected={activeFormStep === formStep}
                        className={`action-tiny${activeFormStep === formStep ? ' active' : ' quaternary'}`}
                        onClick={() => setActiveFormStep(formStep)}
                        key={formStep}
                      >
                        Form step {formStep}
                        <span>{itemCount}</span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className="action-tiny quaternary public-form-add-step"
                    onClick={() => setActiveFormStep(Math.min(20, highestFormStep + 1))}
                    disabled={highestFormStep >= 20}
                  >
                    Add form step
                  </button>
                </div>
                <small>
                  New items are added to the open form step. Changing an item's step also moves the following items from its current step.
                </small>
              </div>
              <div className="public-form-step-heading-settings" aria-label={`Form step ${activeFormStep} heading`}>
                <div className="form-group nm">
                  <label className="subtext" htmlFor={`public-form-step-title-${activeFormStep}`}>Step title</label>
                  <input
                    id={`public-form-step-title-${activeFormStep}`}
                    type="text"
                    value={activeStepTitle}
                    onChange={(event) => updateStepContent('step_title', event.target.value)}
                    maxLength={200}
                    placeholder={activeFormStep === 1 ? draft.title || 'Your information' : `Step ${activeFormStep}`}
                  />
                </div>
                <div className="form-group nm">
                  <label className="subtext" htmlFor={`public-form-step-instructions-${activeFormStep}`}>Step instructions</label>
                  <textarea
                    id={`public-form-step-instructions-${activeFormStep}`}
                    value={activeStepInstructions}
                    onChange={(event) => updateStepContent('step_instruction', event.target.value)}
                    rows={2}
                    maxLength={4000}
                    placeholder={activeFormStep === 1 ? draft.description || 'Optional instructions for this step' : 'Optional instructions for this step'}
                  />
                </div>
                <small>Shown above this step. Subtitles and instructions remain available below for context within the field list.</small>
              </div>
              {errors.fields && <div className="public-form-error" role="alert">{errors.fields}</div>}
              <div
                id={`public-form-step-panel-${activeFormStep}`}
                role="tabpanel"
                aria-labelledby={`public-form-step-tab-${activeFormStep}`}
              >
                {draft.fields.length === 0 && (
                  <div className="public-form-empty-state compact">
                    <Eye size={22} />
                    <strong>Your form preview starts here.</strong>
                    <p>Add writable Reports fields from the catalog.</p>
                  </div>
                )}
                {draft.fields.length > 0 && visibleFieldEntries.length === 0 && (
                  <div className="public-form-empty-state compact">
                    <Eye size={22} />
                    <strong>Form step {activeFormStep} is empty.</strong>
                    <p>Add an item from the catalog. It will be assigned to this form step automatically.</p>
                  </div>
                )}
                {visibleFieldEntries.map(({ field, index }, visibleIndex) => {
                const isCollapsed = collapsedFieldIds.has(field.instance_id);
                const editorId = `public-form-field-editor-${field.instance_id.replace(/[^A-Za-z0-9_-]/g, '-')}`;
                const isContent = isPublicFormContentItem(field);
                const isCustomQuestion = field.source === 'custom_question' || String(field.field_key || '').startsWith('question.');
                if (isContent) {
                  const contentLabel = field.content_type === 'subtitle' ? 'Subtitle' : 'Instructions';
                  return (
                    <article className={`public-form-selected-field public-form-content-item${isCollapsed ? ' collapsed' : ''}`} key={field.instance_id}>
                      <div className="public-form-field-order">
                        <span>{index + 1}</span>
                        <button type="button" className="action-micro quaternary public-form-order-button" onClick={() => moveFieldWithinStep(index, -1)} disabled={visibleIndex === 0} aria-label={`Move ${contentLabel} up`}><ArrowUp size={14} /></button>
                        <button type="button" className="action-micro quaternary public-form-order-button" onClick={() => moveFieldWithinStep(index, 1)} disabled={visibleIndex === visibleFieldEntries.length - 1} aria-label={`Move ${contentLabel} down`}><ArrowDown size={14} /></button>
                      </div>
                      <div className="public-form-field-editor">
                        <div
                          className="action quaternary public-form-field-collapse"
                          onClick={() => toggleFieldCollapsed(field.instance_id)}
                          aria-expanded={!isCollapsed}
                          aria-controls={editorId}
                          aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${contentLabel}`}
                        >
                          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                          <span>
                            <strong>{field.text}</strong>
                            <small>{contentLabel} · Step {field.step} · Display only</small>
                          </span>
                        </div>
                        {!isCollapsed && (
                          <div className="public-form-field-editor-details" id={editorId}>
                            <div className="public-form-two-column">
                              <div className="form-group nm">
                                <label className="subtext" htmlFor={`public-form-content-text-${index}`}>{contentLabel} text</label>
                                {field.content_type === 'instruction' ? (
                                  <textarea
                                    id={`public-form-content-text-${index}`}
                                    value={field.text}
                                    onChange={(event) => updateField(index, { text: event.target.value })}
                                    rows={4}
                                    maxLength={4000}
                                    placeholder="Explain what the recipient should provide in this section"
                                  />
                                ) : (
                                  <input
                                    id={`public-form-content-text-${index}`}
                                    type="text"
                                    value={field.text}
                                    onChange={(event) => updateField(index, { text: event.target.value })}
                                    maxLength={200}
                                    placeholder="Section title"
                                  />
                                )}
                              </div>
                              <div className="form-group nm">
                                <label className="subtext" htmlFor={`public-form-content-step-${index}`}>Form step</label>
                                <input id={`public-form-content-step-${index}`} type="number" min="1" max="20" value={field.step} onChange={(event) => updateFieldStep(index, event.target.value)} placeholder="1" />
                              </div>
                            </div>
                            {field.content_type === 'subtitle' && (
                              <div className="form-group nm full public-form-repeat-group-settings">
                                <Checkbox
                                  value={field.repeat_group?.enabled === true}
                                  onChange={() => updateField(index, {
                                    repeat_group: field.repeat_group?.enabled
                                      ? null
                                      : { enabled: true, entry_label: 'Entry', add_label: 'Add another entry' },
                                  })}
                                  label="Allow additional group entry"
                                  ariaLabel={`${field.text}: allow additional group entry`}
                                />
                                {field.repeat_group?.enabled && (
                                  <div className="public-form-two-column">
                                    <div className="form-group nm">
                                      <label className="subtext" htmlFor={`public-form-group-entry-label-${index}`}>Entry name</label>
                                      <input
                                        id={`public-form-group-entry-label-${index}`}
                                        type="text"
                                        value={field.repeat_group.entry_label || ''}
                                        onChange={(event) => updateField(index, {
                                          repeat_group: { ...field.repeat_group, entry_label: event.target.value },
                                        })}
                                        maxLength={80}
                                        placeholder="Property"
                                      />
                                      <small>Recipients see this as “Property 1,” “Property 2,” and so on.</small>
                                    </div>
                                    <div className="form-group nm">
                                      <label className="subtext" htmlFor={`public-form-group-add-label-${index}`}>Add button text</label>
                                      <input
                                        id={`public-form-group-add-label-${index}`}
                                        type="text"
                                        value={field.repeat_group.add_label || ''}
                                        onChange={(event) => updateField(index, {
                                          repeat_group: { ...field.repeat_group, add_label: event.target.value },
                                        })}
                                        maxLength={120}
                                        placeholder="Add another property"
                                      />
                                    </div>
                                  </div>
                                )}
                                <small>All questions after this subtitle, up to the next subtitle or form step, repeat together. Their required and answer settings stay unchanged.</small>
                              </div>
                            )}
                            <small className="public-form-display-only-note">This item gives recipients context and is never submitted, reviewed as an answer, or added to a report.</small>
                          </div>
                        )}
                      </div>
                      <button type="button" className="action-micro quinary public-form-delete-field" onClick={() => removeField(index)} aria-label={`Remove ${contentLabel}`}><Trash2 size={16} /></button>
                    </article>
                  );
                }
                return (
                  <article className={`public-form-selected-field${isCollapsed ? ' collapsed' : ''}`} key={field.instance_id}>
                    <div className="public-form-field-order">
                      <span>{index + 1}</span>
                      <button type="button" className="action-micro quaternary public-form-order-button" onClick={() => moveFieldWithinStep(index, -1)} disabled={visibleIndex === 0} aria-label={`Move ${field.label} up`}><ArrowUp size={14} /></button>
                      <button type="button" className="action-micro quaternary public-form-order-button" onClick={() => moveFieldWithinStep(index, 1)} disabled={visibleIndex === visibleFieldEntries.length - 1} aria-label={`Move ${field.label} down`}><ArrowDown size={14} /></button>
                    </div>
                    <div className="public-form-field-editor">
                      <div
                        type="button"
                        className="action quaternary public-form-field-collapse"
                        onClick={() => toggleFieldCollapsed(field.instance_id)}
                        aria-expanded={!isCollapsed}
                        aria-controls={editorId}
                        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${field.label}`}
                      >
                        {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        <span>
                          <strong>{field.label}</strong>
                          <small>
                            {publicFormFieldTypeTitle(field.type)} · Step {field.step} · {field.required ? 'Required' : 'Optional'} · {field.repeatable ? 'Repeatable' : 'Single answer'} · {field.condition ? 'Conditional' : 'Always shown'}
                          </small>
                        </span>
                      </div>
                      {!isCollapsed && (
                        <div className="public-form-field-editor-details" id={editorId}>
                          <div className="public-form-two-column">
                            <div className="form-group nm">
                              <label className="subtext" htmlFor={`public-form-field-label-${index}`}>Recipient label</label>
                              <input id={`public-form-field-label-${index}`} type="text" value={field.label} onChange={(event) => updateField(index, { label: event.target.value })} maxLength={160} placeholder="Question shown to the recipient" />
                            </div>
                            <div className="form-group nm">
                              <label className="subtext" htmlFor={`public-form-field-step-${index}`}>Form step</label>
                              <input id={`public-form-field-step-${index}`} type="number" min="1" max="20" value={field.step} onChange={(event) => updateFieldStep(index, event.target.value)} placeholder="1" />
                            </div>
                          </div>
                          {isCustomQuestion && (
                            <PublicFormSelect
                              id={`public-form-field-type-${index}`}
                              label="Question type"
                              className="full"
                              value={field.type}
                              onChange={(event) => updateField(index, {
                                type: event.target.value,
                                repeatable: false,
                              })}
                            >
                              {CUSTOM_QUESTION_TYPE_OPTIONS.map((option) => (
                                <option value={option.value} key={option.value}>{option.label}</option>
                              ))}
                            </PublicFormSelect>
                          )}
                          {isCustomQuestion && ['select', 'checkboxes'].includes(field.type) && (
                            <div className="form-group nm full">
                              <label className="subtext" htmlFor={`public-form-field-options-${index}`}>Answer choices</label>
                              <textarea
                                id={`public-form-field-options-${index}`}
                                value={field.options_text || ''}
                                onChange={(event) => updateField(index, {
                                  options_text: event.target.value,
                                  options: parsePublicFormOptionLines(event.target.value),
                                })}
                                rows={4}
                                maxLength={8000}
                                placeholder={'One choice per line\nFirst choice\nSecond choice'}
                              />
                              <small>Enter one recipient-visible option per line.</small>
                            </div>
                          )}
                          <div className="form-group nm full">
                            <label className="subtext" htmlFor={`public-form-field-help-${index}`}>Help text</label>
                            <input id={`public-form-field-help-${index}`} type="text" value={field.help_text} onChange={(event) => updateField(index, { help_text: event.target.value })} maxLength={500} placeholder="Optional guidance shown below this question" />
                          </div>
                          <div className="public-form-field-options">
                            <div className="form-group nm spec public-form-checkbox-control">
                              <Checkbox
                                value={field.required}
                                onChange={() => updateField(index, { required: !field.required })}
                                label="Required"
                                ariaLabel={`${field.label}: required`}
                              />
                            </div>
                            <div className="form-group nm spec public-form-checkbox-control">
                              <Checkbox
                                value={field.repeatable}
                                onChange={() => updateField(index, { repeatable: !field.repeatable })}
                                label="Allow another answer"
                                ariaLabel={`${field.label}: allow another answer`}
                                disabled={['boolean', 'checkbox', 'checkboxes', 'radio'].includes(field.type)}
                              />
                            </div>
                            <PublicFormSelect
                              id={`public-form-show-condition-${index}`}
                              label="Show condition"
                              value={field.condition?.field_key || ''}
                              onChange={(event) => updateField(index, {
                                condition: event.target.value ? { field_key: event.target.value, operator: 'equals', value: '' } : null,
                              })}
                            >
                              <option value="">Always show</option>
                              {conditionCandidates(index).map((candidate) => (
                                <option value={candidate.field_key} key={candidate.instance_id}>When “{candidate.label}” matches…</option>
                              ))}
                            </PublicFormSelect>
                            {field.condition && (
                              <>
                                <PublicFormSelect
                                  id={`public-form-condition-${index}`}
                                  label="Condition"
                                  value={field.condition.operator}
                                  onChange={(event) => updateField(index, { condition: { ...field.condition, operator: event.target.value } })}
                                >
                                  <option value="equals">Equals</option>
                                  <option value="not_equals">Does not equal</option>
                                  <option value="contains">Contains</option>
                                  <option value="not_empty">Has an answer</option>
                                  <option value="empty">Is empty</option>
                                </PublicFormSelect>
                                {!['not_empty', 'empty'].includes(field.condition.operator) && (
                                  <div className="form-group nm">
                                    <label className="subtext" htmlFor={`public-form-condition-value-${index}`}>Value</label>
                                    <input id={`public-form-condition-value-${index}`} type="text" value={field.condition.value} onChange={(event) => updateField(index, { condition: { ...field.condition, value: event.target.value } })} placeholder="Answer to match" />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <button type="button" className="action-micro quinary public-form-delete-field" onClick={() => removeField(index)} aria-label={`Remove ${field.label}`}><Trash2 size={16} /></button>
                  </article>
                );
                })}
              </div>
            </div>
          </section>
        )}

        {!showLinkHistory && activeStep.id === 'delivery' && (
          <section className="public-form-builder-panel" aria-labelledby="public-form-delivery-title">
            <div className="public-form-panel-heading">
              <div className="sub-title">
                <h4 id="public-form-delivery-title">Secure link and notifications</h4>
              </div>
              <p className="subtext">Links carry their secret in the URL fragment, expire automatically, and can be revoked.</p>
            </div>
            <div className="public-form-security-callout">
              <LockKeyhole size={19} />
              <p>Do not paste a recipient link into a case note or a shared document. CaseDB stores only a hash of its secret.</p>
            </div>
            {draft.target_mode === 'existing_case' && (
              <PublicFormCaseSelect
                value={linkSettings.target_id}
                selectedCase={selectedTargetCase}
                onResolved={setSelectedTargetCase}
                caseTypeId={draft.case_type_id}
                inputRef={targetInputRef}
                disabled={saving || publishing}
                error={errors.target_id}
                onChange={(option) => {
                  setSelectedTargetCase(option);
                  updateLinkSettings({ target_id: option?.value || '' });
                  setErrors((current) => ({ ...current, target_id: undefined }));
                }}
              />
            )}
            {draft.target_mode === 'existing_lead' && (
              <div className={`form-group nm full${errors.target_id ? ' has-error' : ''}`}>
                <label className="subtext" htmlFor="public-form-target-id">{draft.target_mode === 'existing_case' ? 'Case' : 'Lead'} ID for this link <span className="required" aria-hidden="true">*</span></label>
                <input
                  ref={targetInputRef}
                  id="public-form-target-id"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={linkSettings.target_id}
                  onChange={(event) => {
                    updateLinkSettings({ target_id: event.target.value });
                    setErrors((current) => ({ ...current, target_id: undefined }));
                  }}
                  aria-required="true"
                  aria-invalid={Boolean(errors.target_id)}
                  aria-describedby={`public-form-target-help${errors.target_id ? ' public-form-target-error' : ''}`}
                />
                <small id="public-form-target-help">Required to create a link here. Open the existing {draft.target_mode === 'existing_case' ? 'case' : 'lead'} and copy its numeric ID from the address bar. The record must match this form’s case type and you must have access to it. Publishing and previewing do not require this ID or send anything.</small>
                {errors.target_id && <small id="public-form-target-error" role="alert">{errors.target_id}</small>}
              </div>
            )}
            <div className="public-form-two-column">
              <div className={`form-group nm${errors.expires_in_days ? ' has-error' : ''}`}>
                <label className="subtext" htmlFor="public-form-link-lifetime">Link lifetime in days</label>
                <input
                  id="public-form-link-lifetime"
                  type="number"
                  min="1"
                  max="3650"
                  step="1"
                  value={linkSettings.expires_in_days}
                  onChange={(event) => {
                    updateLinkSettings({ expires_in_days: event.target.value });
                    setErrors((current) => ({ ...current, expires_in_days: undefined }));
                  }}
                  aria-invalid={Boolean(errors.expires_in_days)}
                  aria-describedby={errors.expires_in_days ? 'public-form-link-lifetime-error' : undefined}
                />
                <small>Choose any whole number from 1 to 3,650. The default is 7 days.</small>
                {errors.expires_in_days && <small id="public-form-link-lifetime-error" role="alert">{errors.expires_in_days}</small>}
              </div>
              <div className={`form-group nm${errors.max_uses ? ' has-error' : ''}`}>
                <label className="subtext" htmlFor="public-form-max-submissions">Maximum submissions</label>
                <input
                  id="public-form-max-submissions"
                  type="number"
                  min="1"
                  max="1000"
                  step="1"
                  value={linkSettings.max_uses}
                  onChange={(event) => {
                    updateLinkSettings({ max_uses: event.target.value });
                    setErrors((current) => ({ ...current, max_uses: undefined }));
                  }}
                  disabled={linkSettings.unlimited_submissions}
                  aria-invalid={Boolean(errors.max_uses)}
                  aria-describedby={errors.max_uses ? 'public-form-max-submissions-error' : undefined}
                  placeholder="1"
                />
                {errors.max_uses && <small id="public-form-max-submissions-error" role="alert">{errors.max_uses}</small>}
                <Checkbox
                  value={linkSettings.unlimited_submissions}
                  onChange={() => {
                    updateLinkSettings((current) => ({
                      ...current,
                      unlimited_submissions: !current.unlimited_submissions,
                    }));
                    setErrors((current) => ({ ...current, max_uses: undefined }));
                  }}
                  label="Allow unlimited submissions"
                  ariaLabel="Allow unlimited submissions"
                />
              </div>
            </div>
            <fieldset>
              <legend className="sub-title"><h4>Recipient password</h4></legend>
              {passwordLinksEnabled ? (
                <div className="public-form-notification-row">
                  <div className="form-group nm full public-form-checkbox-control">
                    <Checkbox
                      value={linkSettings.password_protected}
                      onChange={() => {
                        updateLinkSettings((current) => {
                          const enabled = !current.password_protected;
                          return {
                            ...current,
                            password_protected: enabled,
                            password: enabled ? current.password : '',
                          };
                        });
                        setErrors((current) => ({ ...current, password: undefined }));
                      }}
                      label="Require a password for this link"
                      ariaLabel="Require a password for this link"
                    />
                  </div>
                  {linkSettings.password_protected && (
                    <div className={`form-group nm full${errors.password ? ' has-error' : ''}`}>
                      <label className="subtext" htmlFor="public-form-password">Link password</label>
                      <input
                        id="public-form-password"
                        type="password"
                        value={linkSettings.password}
                        onChange={(event) => {
                          updateLinkSettings({ password: event.target.value });
                          setErrors((current) => ({ ...current, password: undefined }));
                        }}
                        minLength={12}
                        maxLength={256}
                        autoComplete="new-password"
                        aria-invalid={Boolean(errors.password)}
                        aria-describedby={`public-form-password-help${errors.password ? ' public-form-password-error' : ''}`}
                        placeholder="At least 12 characters"
                      />
                      <small id="public-form-password-help">Share the password separately from the recipient link. CaseDB stores only its Argon2id verifier.</small>
                      {errors.password && <small id="public-form-password-error" role="alert">{errors.password}</small>}
                    </div>
                  )}
                </div>
              ) : (
                <p className="public-form-empty-copy">Password-protected links remain off until the separate password-link feature is approved and enabled.</p>
              )}
            </fieldset>
            <fieldset>
              <legend className="sub-title"><h4>Notify me</h4></legend>
              <div className="public-form-notification-row">
                <div className="form-group nm spec public-form-checkbox-control">
                  <Checkbox
                    value={draft.notify_creator_in_app}
                    onChange={() => setDraftValue('notify_creator_in_app', !draft.notify_creator_in_app)}
                    label="In CaseDB"
                    ariaLabel="Notify me in CaseDB"
                  />
                </div>
                <div className="form-group nm spec public-form-checkbox-control">
                  <Checkbox
                    value={draft.notify_creator_email}
                    onChange={() => setDraftValue('notify_creator_email', !draft.notify_creator_email)}
                    label="By email"
                    ariaLabel="Notify me by email"
                  />
                </div>
                {!emailEnabled && <small>Email preference will be saved, but delivery remains off until the email worker is approved and configured.</small>}
                {!inAppNotificationsEnabled && <small>In-app preference will be saved, but delivery remains off until the notification worker is approved and configured.</small>}
              </div>
            </fieldset>
            <fieldset>
              <legend className="sub-title"><h4>Notify other users</h4></legend>
              {recipients.length === 0 ? (
                <p className="public-form-empty-copy">No eligible notification recipients were returned by the server.</p>
              ) : (
                <>
                  <div className="form-group nm full public-form-recipient-search">
                    <label className="subtext" htmlFor="public-form-recipient-search">Notification recipients</label>
                    <SearchSelect
                      inputId="public-form-recipient-search"
                      value=""
                      options={recipientOptions}
                      returnOptionObject
                      renderOption={renderRecipientOption}
                      onChange={(option) => {
                        const userId = Number(option?.value);
                        if (!selectedRecipientIds.has(userId)) toggleRecipient('in_app', userId);
                      }}
                      disabled={saving || publishing}
                      placeholder="Search users"
                      ariaLabel="Search notification recipients"
                    />
                    <small>Add a user to notify in CaseDB, then choose their notification channels below.</small>
                  </div>
                  {selectedRecipients.length > 0 && (
                    <div className="public-form-recipient-list">
                      {selectedRecipients.map((recipient) => (
                        <div className="public-form-recipient" key={recipient.id}>
                          <span><strong className="small-text">{recipient.name}</strong><small>{recipient.email}</small></span>
                          <div className="form-group nm spec public-form-checkbox-control">
                            <Checkbox
                              value={draft.notification_in_app_user_ids.includes(recipient.id)}
                              disabled={saving || publishing}
                              onChange={() => toggleRecipient('in_app', recipient.id)}
                              label="In CaseDB"
                              ariaLabel={`Notify ${recipient.name} in CaseDB`}
                            />
                          </div>
                          <div className="form-group nm spec public-form-checkbox-control">
                            <Checkbox
                              value={draft.notification_email_user_ids.includes(recipient.id)}
                              disabled={saving || publishing}
                              onChange={() => toggleRecipient('email', recipient.id)}
                              label="Email"
                              ariaLabel={`Notify ${recipient.name} by email`}
                            />
                          </div>
                          <button type="button" className="action-micro quaternary" aria-label={`Remove ${recipient.name} from notifications`} disabled={saving || publishing} onClick={() => removeRecipients([recipient.id])}><X size={14} aria-hidden="true" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {unavailableRecipientIds.length > 0 && <div className="public-form-unavailable-recipients" role="alert">
                <p>{unavailableRecipientIds.length} saved notification recipient(s) no longer have eligible access. Remove them before saving.</p>
                <button type="button" className="action-tiny quaternary" disabled={saving || publishing} onClick={() => removeRecipients(unavailableRecipientIds)}>Remove unavailable recipients</button>
              </div>}
            </fieldset>
            <div className="form-group nm full">
              <label className="subtext" htmlFor="public-form-confirmation">Confirmation message</label>
              <textarea
                id="public-form-confirmation"
                value={draft.confirmation_message}
                onChange={(event) => setDraftValue('confirmation_message', event.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Thanks — your information was received."
              />
            </div>
          </section>
        )}

        {!showLinkHistory && activeStep.id === 'review' && (
          <section className="public-form-builder-panel public-form-review-panel" aria-labelledby="public-form-review-title">
            <div className="public-form-panel-heading">
              <div className="sub-title">
                <h4 id="public-form-review-title">Review and publish</h4>
              </div>
              <p className="subtext">{draft.status === 'published'
                ? (hasCreationSettings
                  ? 'Form-content changes require republishing. New-record defaults only need saving before you create new links.'
                  : 'Saved changes require an explicit republish before new recipient links can use them.')
                : 'Saving keeps this private. Publishing allows you to issue a bounded recipient link.'}</p>
            </div>
            <dl className="public-form-review-grid" aria-label="Form summary">
              <div className="public-form-review-row">
                <dt className="subtext">Public title</dt>
                <dd>
                  <strong className="small-text">{draft.title || 'Untitled form'}</strong>
                  <p className="subtext">{draft.description || 'No introduction added.'}</p>
                </dd>
              </div>
              <div className="public-form-review-row">
                <dt className="subtext">Destination</dt>
                <dd>
                  <strong className="small-text">{publicFormTargetLabel(draft.target_mode)}</strong>
                  <p className="subtext">{['existing_case', 'existing_lead'].includes(draft.target_mode)
                    ? (linkSettings.target_id
                      ? draft.target_mode === 'existing_case' && selectedTargetCase?.value === String(linkSettings.target_id)
                        ? `This link targets: ${selectedTargetCase.label} (${linkSettings.target_id})`
                        : `This link targets ID: ${linkSettings.target_id}`
                      : 'Choose an existing record in Link & alerts before creating a recipient link.')
                    : creationLinkEnabled && capabilities.field_application === true
                      ? `CaseDB will create a new ${draft.target_mode === 'create_case' ? 'case' : 'lead'} and save supported answers when the recipient submits.`
                      : 'New-record submission processing remains unavailable until the creation runtime is enabled.'}</p>
                </dd>
              </div>
              <div className="public-form-review-row">
                <dt className="subtext">Questions</dt>
                <dd className="public-form-review-inline">
                  <strong className="small-text">{questionFields.length} question{questionFields.length === 1 ? '' : 's'}</strong>
                  <p className="subtext">{draft.fields.filter(isPublicFormContentItem).length} display-only item(s) · {Math.max(1, ...draft.fields.map((field) => field.step))} recipient step(s)</p>
                </dd>
              </div>
              <div className="public-form-review-row">
                <dt className="subtext">Link controls</dt>
                <dd className="public-form-review-inline">
                  <strong className="small-text">{linkSettings.unlimited_submissions ? 'Unlimited submissions' : `${linkSettings.max_uses} submission${Number(linkSettings.max_uses) === 1 ? '' : 's'}`}</strong>
                  <p className="subtext">Expires after {linkSettings.expires_in_days} day(s){linkSettings.password_protected ? ' · Password required' : ''}</p>
                </dd>
              </div>
              {hasCreationSettings && (
                <div className="public-form-review-row">
                  <dt className="subtext">New-record defaults</dt>
                  <dd>
                    <dl className="public-form-review-defaults">
                      {CREATION_SETTING_FIELDS.map((setting) => {
                        const value = draft.creation_settings[setting.key];
                        const selected = availableCreationOptions[setting.options].find((option) => option.id === value);
                        return (
                          <div key={setting.key}>
                            <dt className="subtext">{setting.label}</dt>
                            <dd>{selected?.name || (value ? `Unavailable selection (ID ${value})` : setting.required ? 'Not selected' : 'None')}</dd>
                          </div>
                        );
                      })}
                    </dl>
                  </dd>
                </div>
              )}
            </dl>
            <p className="subtext">{questionFields.length - standaloneQuestions} CaseDB-linked question(s) · {standaloneQuestions} standalone question(s). Standalone questions collect submission answers; they do not automatically create or map a CaseDB field. Repeating answers are also kept for review.{capabilities.field_application === true ? ' Supported non-repeating linked fields fill empty values only; existing values are not overwritten.' : ' Automatic field application is not enabled in this environment.'}</p>
            {placeholderLabels > 0 && <p className="subtext">{placeholderLabels} question(s) still use a placeholder label such as “Custom question” or “Untitled field”. Rename these in Fields so recipients know what to answer.</p>}
            <div className="public-form-review-fields">
              {draft.fields.map((field, index) => (
                <div key={field.instance_id}>
                  <span>{index + 1}</span>
                  <strong>{isPublicFormContentItem(field) ? field.text : field.label}</strong>
                  <small>{publicFormFieldTypeTitle(field.type)}{isPublicFormContentItem(field) ? ' · display only' : ''}{field.required ? ' · required' : ''}{field.repeatable ? ' · repeatable' : ''}</small>
                </div>
              ))}
            </div>
            <div className="public-form-publish-actions">
              <button type="button" className="action quaternary" onClick={saveDraft} disabled={saving || publishing}>
                {saving ? <Loader2 size={16} className="spinner" /> : <Save size={16} />} Save draft
              </button>
              <button type="button" className="action" onClick={publish} disabled={saving || publishing || publicationCurrent}>
                {publishing ? <Loader2 size={16} className="spinner" /> : <Send size={16} />} {publicationCurrent ? 'Published' : draft.status === 'published' ? 'Republish form' : 'Publish form'}
              </button>
              <button
                type="button"
                className="action quaternary"
                onClick={issueLink}
                disabled={!publicationCurrent || saving || publishing || issuing || !publicRuntimeEnabled || !creationLinkEnabled || (hasCreationSettings && creationSettingsDirty) || (['existing_case', 'existing_lead'].includes(draft.target_mode) && !Number(linkSettings.target_id))}
                aria-busy={issuing}
              >
                {issuing ? <Loader2 size={16} className="spinner" /> : <Link2 size={16} />} Create recipient link
              </button>
            </div>
            {hasCreationSettings && creationSettingsDirty && (
              <div className="public-form-config-note" role="status">
                <Save size={18} />
                <p>Save these new-record defaults before creating a recipient link. Existing links will not change.</p>
              </div>
            )}
            {draft.status === 'published' && !publicationCurrent && (
              <div className="public-form-config-note">
                <LockKeyhole size={18} />
                <p><strong>Republish required.</strong> Existing links remain pinned to their issued schema. New links stay disabled until these changes are saved and republished.</p>
              </div>
            )}
            {publicationCurrent && ['existing_case', 'existing_lead'].includes(draft.target_mode) && !Number(linkSettings.target_id) && (
              <div className="public-form-config-note">
                <Link2 size={18} />
                <div>
                  <p><strong>Choose an existing {draft.target_mode === 'existing_case' ? 'case' : 'lead'} to create a link.</strong> This form is for an existing record; it does not create a new lead or case. Publishing does not share or send the form. Preview form works without a destination and saves nothing.</p>
                  <button type="button" className="action quaternary" onClick={() => { setStepIndex(2); setFocusTarget(true); }}>Choose {draft.target_mode === 'existing_case' ? 'case' : 'lead'}</button>
                  <p>Alternatively, a configured Public form request field on the correct matter can bind and deliver its own link.</p>
                </div>
              </div>
            )}
            {!publicRuntimeEnabled && (
              <div className="public-form-config-note">
                <LockKeyhole size={18} />
                <p><strong>Recipient links are off.</strong> Publishing is safe, but link creation remains disabled until the separate public runtime and origin configuration are enabled.</p>
              </div>
            )}
            {publicRuntimeEnabled && !creationLinkEnabled && (
              <div className="public-form-config-note">
                <LockKeyhole size={18} />
                <p><strong>New-record links are off.</strong> Creation-mode processing is disabled in this environment. Ask the system administrator to check the runtime configuration; signing in as an admin does not enable it.</p>
              </div>
            )}
            {issuedLink && (
              <div className="public-form-issued-link" ref={issuedLinkRef}>
                <span className="sr-only" role="status">Recipient link created. The copy button is ready.</span>
                <div>
                  <strong>Copy this link now</strong>
                  <p>{issuedLink.passwordProtected
                    ? 'Its secret is shown only for delivery. Send the separate password through another channel.'
                    : 'Its secret is shown only for delivery. Treat it like a password.'}</p>
                </div>
                <div className="form-group nm public-form-issued-link-control">
                  <label className="sr-only" htmlFor="public-form-issued-link-value">Issued public form link</label>
                  <input id="public-form-issued-link-value" type="text" value={issuedLink.url} readOnly onFocus={(event) => event.target.select()} />
                </div>
                <button ref={issuedLinkCopyRef} type="button" className="action quaternary" onClick={copyLink}><Clipboard size={15} /> {copyState || 'Copy link'}</button>
              </div>
            )}
          </section>
        )}
      </div>
      {previewDraft && <PublicFormPreview draft={previewDraft} onClose={() => setPreviewDraft(null)} />}
    </div>
  );
};

export default PublicFormBuilder;
